import React, { useState, useEffect } from 'react';
import { SearchResponse, TypeSearch, walletRepository } from '../../../../repositories/WalletRepository';
import { useAuth } from '../../../../hooks/useAuth';
import { OPAAvatar } from '../../../../components/OPAAvatar';
import { LatestRecipients } from './LatestRecipients';
import { Pleb } from '../../../../models/Pleb';
import { getNameToShow, SendTransactionStateRecipient } from '../SendTransaction';
import { BulkPayment, BulkTypes } from '../../../../models/BulkPayment';
import { getFlagEmoji } from '../../../../utils/Utils';

interface InputSendWalletProps {
    onChangeLnurl: (lnurl: string) => void;
    onSelectLnurl: (lnurl: string) => void;
    onChangePleb: (pleb: Pleb) => void;
    onSelectPleb: (pleb: Pleb) => void;
    onSelectBulkPayment: (bulkPayment: BulkPayment) => void;
    defaultRecipient?: SendTransactionStateRecipient;
}

export const InputSendWallet: React.FC<InputSendWalletProps> = ({ onChangeLnurl, onSelectLnurl, onChangePleb, onSelectPleb, onSelectBulkPayment, defaultRecipient }) => {
    const [value, setValue] = useState(getNameToShow(defaultRecipient));
    const [isFocused, setIsFocused] = useState(false);
    const [showListbox, setShowListbox] = useState(false);
    const {pleb} = useAuth();
    const [searchResponses, setSearchResponses] = useState<SearchResponse[]>([]);
    const [plebSelected, setPlebSelected] = useState<Pleb | null>(defaultRecipient?.pleb ?? null);
    
    useEffect(() => {
        // Create new abort controller for each effect
        const abortController = new AbortController();

        const searchWallet = async () => {
            if (value.length > 0) {
                try {
                    const searchResponses = await walletRepository.searchWallet(
                        pleb?.id ?? '', 
                        value, 
                        abortController.signal
                    );
                    setSearchResponses(searchResponses);
                } catch (error) {
                    // Ignore aborted requests
                    if (error instanceof Error && error.name === 'AbortError') {
                        console.log('Search request aborted');
                        return;
                    }
                    console.error('Search error:', error);
                }
            } else {
                setSearchResponses([]);
            }
        };

        searchWallet();

        // Cleanup: abort any pending request when value changes or component unmounts
        return () => {
            abortController.abort();
        };
    }, [value, pleb?.id]);

    useEffect(() => {
        if(plebSelected) {
            onChangePleb(plebSelected);
        } else {
            onChangeLnurl(value);
        }
    }, [plebSelected, value, onChangeLnurl, onChangePleb]);

    const onClear = () => {
        setValue('');
        setPlebSelected(null);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        setShowListbox(true);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        // Delay hiding listbox to allow click events
        setTimeout(() => setShowListbox(false), 200);
    };

    const handleSelectSearchResponse = (searchResponse: SearchResponse) => {
        switch(searchResponse.type) {
            case TypeSearch.user:
                handleSelectPleb(searchResponse.user);
                break;
            case TypeSearch.bulk_special:
                handleSelectBulkPayment(searchResponse.bulkSpecial);
                break;
            case TypeSearch.city:
                handleSelectBulkPayment(new BulkPayment({
                    id: searchResponse.city?.id ?? '',
                    type: BulkTypes.city,
                    name: searchResponse.city?.name ?? '',
                    recipientsCount: 0,
                }));
                break;
            case TypeSearch.distanceFromMyself:
                handleSelectBulkPayment(new BulkPayment({
                    id: searchResponse.distanceFromMyself?.id ?? '',
                    type: BulkTypes.distanceFromMyself,
                    name: searchResponse.distanceFromMyself?.name ?? '',
                    recipientsCount: 0,
                }));
                break;
        }
    }

    const handleSelectPleb = (element?: Pleb) => {
        if(element) {
            setValue(element.getLabelToShowSendToWallet());
            setPlebSelected(element);
            setShowListbox(false);
            onSelectPleb(element);
        }
    };

    const handleSelectBulkPayment = (element?: BulkPayment) => {
        if(element) {
            setValue(element.getDisplayName());
            setPlebSelected(null);
            setShowListbox(false);
            onSelectBulkPayment(element);
        }
    };

    const handleSelectLnurl = (lnurl: string) => {
        setValue(lnurl);
        setPlebSelected(null);
        setShowListbox(false);
        onSelectLnurl(lnurl);
    };

    const handlePaste = async () => {
        try {
          const clipboardText = await navigator.clipboard.readText();
          if (clipboardText) {
            setPlebSelected(null);
            setValue(clipboardText);
          }
        } catch (err) {
          console.error('Clipboard access failed:', err);
        }
    };

    return (
        <div className="relative">
            <div className="relative">
                <input
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className={`
                        w-full
                        px-4
                        py-3
                        pe-9
                        bg-brand-bg-3
                        rounded-xl
                        placeholder:text-brand-color-2
                        focus:outline-none
                        ${isFocused ? '' : ''}
                    `}
                    placeholder="Username, LNURL, LN Address, Country, City or Invoice"
                    value={value}
                    onChange={(e) => {
                        setPlebSelected(null);
                        setValue(e.target.value);
                    }}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    inputMode="text"
                    name="search-wallet"
                />
                {value !== '' && (
                    <button 
                        className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-transparent hover:opacity-70 focus:outline-none flex flex-col text-[20px] p-2"
                        onClick={() => onClear()}
                        type="button"
                    >
                        <i className="material-icons">cancel</i>
                    </button>
                )}
                {value === '' && (
                    <button 
                        className="text-brand-color-2 absolute right-0 top-1/2 transform -translate-y-1/2 bg-transparent hover:opacity-70 focus:outline-none flex flex-col text-[20px] p-2"
                        onClick={handlePaste}
                        type="button"
                    >
                        <i className="material-icons">content_paste</i>
                    </button>
                )}
            </div>

            {/* Listbox dropdown */}
            {showListbox && searchResponses.length > 0 && (
                <div className="w-full mt-1 rounded-lg z-50 max-h-60 overflow-y-auto">
                {searchResponses.map((searchResponse, index) => {
                    switch(searchResponse.type) {
                        case TypeSearch.user:
                            return (
                                <div
                                    key={index}
                                    onClick={() => handleSelectSearchResponse(searchResponse)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    <OPAAvatar
                                        imageUrl={searchResponse.user?.thumbnail}
                                        size={40}
                                        borderWidth={0}
                                        borderColor="var(--brand-bg-2)"
                                    />
                                    {searchResponse.user?.name}
                                </div>
                            );
                        case TypeSearch.bulk_special:
                            return (
                                <div 
                                    key={index} 
                                    onClick={() => handleSelectSearchResponse(searchResponse)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    {
                                        /*searchResponse?.imgUrl ? (
                                            <OPAAvatar
                                                imageUrl={searchResponse?.imgUrl}
                                                size={40}
                                                borderWidth={0}
                                                borderColor="var(--brand-bg-2)"
                                            />
                                        ) : (
                                            null
                                        )*/
                                    }
                                    {searchResponse.bulkSpecial?.getDisplayName()}
                                </div>
                            );
                        case TypeSearch.city:
                            return (
                                <div 
                                    key={index} 
                                    onClick={() => handleSelectSearchResponse(searchResponse)}
                                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                >
                                    <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center overflow-hidden">
                                        {
                                            searchResponse.city?.geoZap?.addressType === 'country' ? (
                                                searchResponse.city.geoZap.countryImgUrl ? (
                                                    <img src={searchResponse.city.geoZap.countryImgUrl} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-2xl">{getFlagEmoji(searchResponse.city?.geoZap?.countryCode ?? '')}</span>
                                                )
                                            ) : (
                                                <span className="material-symbols-outlined">location_city</span>
                                            )
                                        }
                                    </div>
                                    {searchResponse.city?.name}
                                </div>
                            );
                            case TypeSearch.distanceFromMyself:
                                return (
                                    <div 
                                        key={index} 
                                        onClick={() => handleSelectSearchResponse(searchResponse)}
                                        className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-brand-bg-4 rounded-lg"
                                    >
                                        <div className="shrink-0 flex items-center w-[40px] h-[40px] bg-brand-bg-4 rounded-full justify-center">
                                            <span className="material-symbols-outlined">location_on</span>
                                        </div>
                                        {searchResponse.distanceFromMyself?.name}
                                    </div>
                                );
                    }
                })}
                </div>
            )}

            <LatestRecipients 
                onSelectPleb={(pleb: Pleb) => handleSelectPleb(pleb)}
                onSelectLnurl={(lnurl: string) => handleSelectLnurl(lnurl)}
                className={`${value !== "" || (showListbox && searchResponses.length > 0) ? 'hidden' : ''}`}
            />
        </div>
    );
};